import { z } from 'zod'
import { jobTypeSchema } from '../enums/job-type.enum.js'
import { moneyAmountSchema } from '../validators/money.validator.js'
import { treeWorkChoiceSchema } from '../enums/tree-work-choice.enum.js'
import { proposalPlanSchema } from '../enums/proposal-plan.enum.js'

/**
 * What a client accepted, and what they declined.
 *
 * Read back on the proposal screen so the business can see the answer, and by
 * the shared page itself so a client returning to the link sees what they
 * already agreed rather than a fresh proposal.
 */
export const proposalSelectionDtoSchema = z.object({
  excludedSites: z.array(z.string()),
  excludedSpecies: z.array(z.string()),
  excludedJobTypes: z.array(jobTypeSchema),
  excludedProblems: z.array(z.string()),
  /** Per-tree decisions, where they differed from what was offered. */
  treeChoices: z.array(
    z.object({
      site: z.string(),
      label: z.string(),
      choice: treeWorkChoiceSchema,
    }),
  ),
  /**
   * Trees switched off one by one. Empty on an answer recorded before the
   * control existed.
   */
  excludedTrees: z
    .array(z.object({ site: z.string(), label: z.string() }).strict())
    .default([]),
  /** Individual rings, by the id frozen on the document. */
  excludedAreas: z
    .array(z.object({ site: z.string(), areaId: z.string() }).strict())
    .default([]),
  /** Every ring of this name on this site. */
  excludedAreaNames: z
    .array(z.object({ site: z.string(), name: z.string() }).strict())
    .default([]),
  /** Irrigation declined: all of it, by kind, by zone, or repair by repair. */
  excludedIrrigation: z.boolean().default(false),
  excludedIrrigationKinds: z.array(z.string()).default([]),
  excludedZones: z
    .array(z.object({ site: z.string(), zone: z.string() }).strict())
    .default([]),
  excludedRepairs: z
    .array(z.object({ site: z.string(), key: z.string() }).strict())
    .default([]),
  /** The plan the client started from; null before plans existed. */
  plan: proposalPlanSchema.nullable().default(null),
  /** The budget they fitted the plan to, in dollars, when they did. */
  budgetAmount: moneyAmountSchema.nullable().default(null),
  /** The agreed figure, as computed by the API. */
  acceptedAmount: moneyAmountSchema.nullable(),
  /** What was declined, in money. */
  deferredAmount: moneyAmountSchema.nullable(),
  acceptedAt: z.string().datetime(),
})

export type ProposalSelectionDto = z.infer<typeof proposalSelectionDtoSchema>
